// Becas en la ficha del alumno (7-oct-2026, Mabel con Max Rodriguez: «hay manera de poner algún botón
// de becado y no le cobre ya que aparece en deudores»). La autoriza Karla; la registra cualquiera que
// cobre. La regla (qué mes cubre, cuánto) vive en el backend: lib/letsmoveBecas.js.
// Plan: /root/forobeta-ops/letsmove/PLAN-BECAS-20261007.md
import { useState } from 'react';
import { GraduationCap, X, AlertCircle, History, CalendarClock } from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import { money } from '../../finances/api';
import {
  Beca, BecaAdeudo, BecaInput, previewBeca, createBeca, updateBeca, revokeBeca,
} from '../api';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

const hoyTj = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' });
export const fechaCorta = (ymd: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
const mesLargo = (ym: string) => {
  const s = new Date(`${ym}-01T12:00:00`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const cubre = (b: Pick<Beca, 'covers_membership' | 'covers_registration'>) =>
  [b.covers_membership && 'mensualidades', b.covers_registration && 'inscripción'].filter(Boolean).join(' e ');

/** Chip del encabezado de la ficha: «🎓 Becado 100 % · hasta 19 dic». */
export function BecaChip({ beca }: { beca: Beca | null | undefined }) {
  if (!beca) return null;
  return (
    <span title={`${beca.vigente ? 'Beca' : 'Beca próxima'}: ${beca.pct}% de ${cubre(beca)}, del ${fechaCorta(beca.start_date)} al ${fechaCorta(beca.end_date)} · ${beca.reason}`}
      className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300">
      <GraduationCap size={13} />
      {beca.vigente ? 'Becado' : 'Beca desde ' + fechaCorta(beca.start_date)} {beca.pct < 100 ? `${beca.pct}%` : ''} · hasta {fechaCorta(beca.end_date)}
    </span>
  );
}

interface Props {
  memberId: number;
  nombre: string;
  beca: Beca | null;
  becas: Beca[];
  finCiclo: string | null;
  puedeEditar: boolean;
  onCambio: () => void;
}

/** Bloque «Beca» de la pestaña Pagos: estado + Dar beca / Cambiar fecha / Quitar + historial. */
export default function BecaFicha({ memberId, nombre, beca, becas, finCiclo, puedeEditar, onCambio }: Props) {
  const toast = useToast();
  const [modal, setModal] = useState<null | 'nueva' | 'editar'>(null);
  const [quitando, setQuitando] = useState(false);
  const [motivoQuitar, setMotivoQuitar] = useState('');
  const [saving, setSaving] = useState(false);
  const [verHistorial, setVerHistorial] = useState(false);
  const pasadas = becas.filter((b) => b.scholarship_id !== beca?.scholarship_id);

  async function quitar() {
    if (!beca || !motivoQuitar.trim()) return;
    setSaving(true);
    try {
      await revokeBeca(memberId, beca.scholarship_id, motivoQuitar.trim());
      toast.success('Beca retirada: desde el próximo mes se vuelve a cobrar normal');
      setQuitando(false); setMotivoQuitar(''); onCambio();
    } catch (e: any) { toast.error(e?.message || 'No se pudo quitar la beca'); }
    finally { setSaving(false); }
  }

  if (!beca && !pasadas.length && !puedeEditar) return null;

  return (
    <div className="mb-5 overflow-hidden rounded-xl border border-violet-500/25 bg-violet-500/[0.04]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <div className="min-w-0">
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-content">
            <GraduationCap size={15} className="text-violet-600 dark:text-violet-400" />
            {beca ? (beca.vigente ? `Becado ${beca.pct}%` : `Beca ${beca.pct}% (empieza ${fechaCorta(beca.start_date)})`) : 'Sin beca'}
          </h4>
          {beca && (
            <p className="mt-0.5 text-xs text-content-2">
              Cubre {cubre(beca)} · del {fechaCorta(beca.start_date)} al <b>{fechaCorta(beca.end_date)}</b> · {beca.reason}
              {beca.created_by_name ? <span className="text-content-3"> · la registró {beca.created_by_name}</span> : null}
            </p>
          )}
          {beca && <p className="mt-0.5 text-[11px] text-content-3">Al terminar, vuelve a cobrar normal solo.</p>}
        </div>
        {puedeEditar && (
          <div className="flex flex-wrap gap-2">
            {beca ? (
              <>
                <button onClick={() => setModal('editar')}
                  className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-content transition hover:bg-surface">
                  <CalendarClock size={14} /> Cambiar
                </button>
                <button onClick={() => setQuitando((v) => !v)}
                  className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-500/10 dark:text-red-400">
                  Quitar beca
                </button>
              </>
            ) : (
              <button onClick={() => setModal('nueva')}
                className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-violet-600/90">
                <GraduationCap size={14} /> Dar beca
              </button>
            )}
          </div>
        )}
      </div>

      {quitando && beca && (
        <div className="border-t border-violet-500/20 px-4 py-3">
          <label className="mb-1 block text-xs font-semibold text-content-2">¿Por qué se quita?</label>
          <div className="flex gap-2">
            <input autoFocus value={motivoQuitar} onChange={(e) => setMotivoQuitar(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') quitar(); if (e.key === 'Escape') setQuitando(false); }}
              placeholder="Ej. dejó de cumplir las condiciones" className={inputCls} />
            <button onClick={quitar} disabled={!motivoQuitar.trim() || saving}
              className="shrink-0 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">Quitar</button>
          </div>
          <p className="mt-1 text-[11px] text-content-3">Los meses ya perdonados se quedan perdonados; desde el siguiente se cobra normal. Queda en el historial.</p>
        </div>
      )}

      {pasadas.length > 0 && (
        <div className="border-t border-violet-500/20 px-4 py-2">
          <button onClick={() => setVerHistorial((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-content-3 hover:text-content">
            <History size={12} /> Historial de becas ({pasadas.length})
          </button>
          {verHistorial && (
            <ul className="mt-1.5 space-y-1 text-xs text-content-2">
              {pasadas.map((b) => (
                <li key={b.scholarship_id}>
                  {b.pct}% de {cubre(b)} · {fechaCorta(b.start_date)} → {fechaCorta(b.end_date)} · {b.reason}
                  {b.revoked_at && <span className="text-red-600 dark:text-red-400"> · quitada{b.revoke_reason ? `: ${b.revoke_reason}` : ''}{b.revoked_by_name ? ` (${b.revoked_by_name})` : ''}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {modal && (
        <BecaModal memberId={memberId} nombre={nombre} finCiclo={finCiclo}
          actual={modal === 'editar' ? beca : null}
          onClose={() => setModal(null)} onGuardado={() => { setModal(null); onCambio(); }} />
      )}
    </div>
  );
}

function BecaModal({ memberId, nombre, finCiclo, actual, onClose, onGuardado }: {
  memberId: number; nombre: string; finCiclo: string | null; actual: Beca | null;
  onClose: () => void; onGuardado: () => void;
}) {
  const toast = useToast();
  const [f, setF] = useState<BecaInput>(() => actual
    ? { pct: actual.pct, start_date: actual.start_date, end_date: actual.end_date,
        covers_membership: actual.covers_membership, covers_registration: actual.covers_registration, reason: actual.reason }
    : { pct: 100, start_date: hoyTj(), end_date: finCiclo || '', covers_membership: true, covers_registration: true, reason: '' });
  const [paso, setPaso] = useState<1 | 2>(1);
  const [afectados, setAfectados] = useState<BecaAdeudo[]>([]);
  const [elegidos, setElegidos] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = f.pct > 0 && f.pct <= 100 && f.start_date && f.end_date && f.end_date >= f.start_date
    && (f.covers_membership || f.covers_registration) && f.reason.trim();

  async function siguiente() {
    setError(null); setSaving(true);
    try {
      const r = await previewBeca(memberId, { ...f, reason: f.reason.trim(), ...(actual ? { scholarship_id: actual.scholarship_id } : {}) });
      const lista = r.afectados || [];
      if (!lista.length) { await guardar([]); return; }
      setAfectados(lista); setElegidos(new Set(lista.map((d) => d.debt_id))); setPaso(2);
    } catch (e: any) { setError(e?.message || 'No se pudo revisar la beca'); }
    finally { setSaving(false); }
  }

  async function guardar(ids: number[]) {
    setSaving(true); setError(null);
    try {
      const body = { ...f, reason: f.reason.trim(), apply_debt_ids: ids };
      const r = actual ? await updateBeca(memberId, actual.scholarship_id, body) : await createBeca(memberId, body);
      const n = r.adeudos?.length || 0;
      toast.success(`${actual ? 'Beca actualizada' : 'Beca registrada'}${n ? ` · ${n} adeudo${n > 1 ? 's' : ''} ${n > 1 ? 'perdonados/ajustados' : 'perdonado/ajustado'}` : ''}`);
      onGuardado();
    } catch (e: any) { setError(e?.message || 'No se pudo guardar la beca'); }
    finally { setSaving(false); }
  }

  const toggle = (id: number) => setElegidos((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const etiqueta = (d: BecaAdeudo) => d.debt_type === 'late_fee' ? `Pago tardío · ${mesLargo(d.month)}` : `Mensualidad · ${mesLargo(d.month)}`;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            <GraduationCap size={18} className="text-violet-600 dark:text-violet-400" /> {actual ? 'Cambiar beca' : 'Dar beca'} · {nombre}
          </h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {paso === 1 ? (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-content-2">Porcentaje</label>
              <div className="flex flex-wrap items-center gap-2">
                {[100, 50].map((p) => (
                  <button key={p} type="button" onClick={() => setF({ ...f, pct: p })}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${f.pct === p ? 'border-violet-500 bg-violet-500/15 text-violet-700 dark:text-violet-300' : 'border-line text-content-2'}`}>
                    {p === 100 ? 'Completa (100 %)' : '50 %'}
                  </button>
                ))}
                <span className="text-xs text-content-3">otro:</span>
                <input type="number" min={1} max={100} value={f.pct} onChange={(e) => setF({ ...f, pct: Number(e.target.value) })}
                  className="w-20 rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
                <span className="text-sm text-content-3">%</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-content-2">Desde</label>
                <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-content-2">Hasta</label>
                <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={inputCls} />
                {finCiclo && f.end_date !== finCiclo && (
                  <button type="button" onClick={() => setF({ ...f, end_date: finCiclo })} className="mt-1 text-[11px] font-semibold text-violet-700 hover:underline dark:text-violet-300">
                    Fin del ciclo ({fechaCorta(finCiclo)})
                  </button>
                )}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-semibold text-content-2">Cubre</span>
              <label className="mr-4 inline-flex items-center gap-1.5 text-sm text-content">
                <input type="checkbox" checked={f.covers_membership} onChange={(e) => setF({ ...f, covers_membership: e.target.checked })} /> Mensualidades
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm text-content">
                <input type="checkbox" checked={f.covers_registration} onChange={(e) => setF({ ...f, covers_registration: e.target.checked })} /> Inscripción
              </label>
              <p className="mt-1 text-[11px] text-content-3">No cubre vitrina, clases sueltas ni eventos.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-content-2">Motivo</label>
              <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
                placeholder="Ej. Autorizada por Karla · beca de semestre" className={inputCls} />
            </div>
            <p className="rounded-lg bg-violet-500/[0.06] px-3 py-2 text-xs text-content-2">
              Mientras dure, {f.pct >= 100 ? 'no se le genera mensualidad ni pago tardío' : `su mensualidad baja ${f.pct} %`}
              {f.covers_registration ? ' y la inscripción sale con la beca aplicada' : ''}. Al terminar vuelve a cobrar normal solo.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-content-2">Cancelar</button>
              <button onClick={siguiente} disabled={!valido || saving}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {saving ? 'Revisando…' : 'Continuar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-content">
              Tiene <b>{afectados.length}</b> adeudo{afectados.length > 1 ? 's' : ''} abierto{afectados.length > 1 ? 's' : ''} en meses que cubre la beca.
              Marca los que se {f.pct >= 100 ? 'perdonan' : 'ajustan'}:
            </p>
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
              {afectados.map((d) => (
                <li key={d.debt_id} className="flex items-center gap-3 px-3 py-2">
                  <input type="checkbox" checked={elegidos.has(d.debt_id)} onChange={() => toggle(d.debt_id)} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-content">{etiqueta(d)}</p>
                    <p className="text-xs text-content-3">
                      {d.accion === 'cancelar' ? <>Se cancela {money(d.amount)}</> : <>{money(d.amount)} → {money(d.nuevo)}</>}
                      {d.abonado > 0 ? ` · ya abonó ${money(d.abonado)}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-content-3">No se borra nada: quedan cancelados con el motivo «Beca», la fecha y quién la registró.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPaso(1)} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-content-2">Atrás</button>
              <button onClick={() => guardar([...elegidos])} disabled={saving}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {saving ? 'Guardando…' : actual ? 'Guardar cambio' : 'Dar beca'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
